import React, { useEffect, useState } from 'react';
import { Button, Input, Combobox, Modal, Table } from '../../components/common';
import { documentSeriesService } from '../../services/resources';
import { apiError } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const TYPES = ['BUY', 'SELL', 'TRANSFER', 'BIAYA', 'PENDAPATAN', 'REMITTANCE'].map((t) => ({ value: t, label: t }));

export default function DocumentSeriesTab({ onToast }) {
  const { can } = useAuth();
  const canCreate = can('SETTINGS', 'CanCreate');
  const canEdit = can('SETTINGS', 'CanEdit');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ transactionType: '', prefix: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await documentSeriesService.list());
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    setForm({ transactionType: '', prefix: '' });
    setError('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ transactionType: row.TransactionType, prefix: row.Prefix });
    setError('');
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await documentSeriesService.update(editing.SeriesID, form);
        onToast?.('Series updated');
      } else {
        await documentSeriesService.create(form);
        onToast?.('Series created');
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(apiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (d) => (d ? String(d).slice(0, 10) : '');

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Numbering infrastructure for transactions (Phase 2). Changing a prefix resets its start
          date to today; numbering restarts from 1 while old documents keep their numbers.
        </p>
        {canCreate && <Button onClick={openCreate}>+ New</Button>}
      </div>
      <Table
        loading={loading}
        rows={rows}
        columns={[
          { key: 'TransactionType', label: 'Transaction Type' },
          { key: 'Prefix', label: 'Prefix' },
          { key: 'StartDate', label: 'Start Date', render: (r) => fmtDate(r.StartDate) },
          { key: 'UpdatedBy', label: 'Updated By' },
          {
            key: '__a',
            label: 'Actions',
            className: 'text-right',
            render: (r) =>
              canEdit ? (
                <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
                  Edit
                </Button>
              ) : null,
          },
        ]}
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? 'Edit Series' : 'New Series'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-3">
          {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <Combobox
            label="Transaction Type"
            required
            placeholder="-- select --"
            options={TYPES}
            value={form.transactionType}
            onChange={(v) => setForm({ ...form, transactionType: v })}
          />
          <Input
            label="Prefix"
            required
            value={form.prefix}
            onChange={(e) => setForm({ ...form, prefix: e.target.value })}
          />
        </form>
      </Modal>
    </div>
  );
}
